import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './ui/input';
import { MapPin, Loader2, X } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Address Autocomplete component using OpenStreetMap Nominatim API (free/open-source)
 * Respects Nominatim usage policy: max 1 request per second, include app identification
 */
const AddressAutocomplete = ({
  value,
  onChange,
  placeholder = 'Start typing an address...',
  className,
  disabled = false,
  countryCode = 'au', // Default to Australia
  ...props
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Update input when external value changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search function
  const searchAddresses = useCallback(async (query) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      // Nominatim API - free, OpenStreetMap-based
      // Usage policy: max 1 request/second, include User-Agent/Referer
      const params = new URLSearchParams({
        q: query,
        format: 'json',
        addressdetails: '1',
        limit: '5',
        countrycodes: countryCode,
      });

      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?${params}`,
        {
          headers: {
            'Accept': 'application/json',
            // Nominatim requires identification
            'User-Agent': 'TTouchCleaningServicesApp/1.0',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch addresses');
      }

      const data = await response.json();
      
      // Format results for display
      const formatted = data.map((item) => ({
        id: item.place_id,
        display_name: item.display_name,
        // Create a cleaner short address
        short_address: formatShortAddress(item),
        lat: item.lat,
        lon: item.lon,
        address: item.address,
      }));

      setSuggestions(formatted);
    } catch (error) {
      console.error('Address search error:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [countryCode]);

  // Format a shorter, cleaner address
  const formatShortAddress = (item) => {
    const addr = item.address || {};
    const parts = [];

    // House number + road
    if (addr.house_number && addr.road) {
      parts.push(`${addr.house_number} ${addr.road}`);
    } else if (addr.road) {
      parts.push(addr.road);
    }

    // Suburb/city
    const suburb = addr.suburb || addr.city || addr.town || addr.village || addr.municipality;
    if (suburb) {
      parts.push(suburb);
    }

    // State
    if (addr.state) {
      parts.push(addr.state);
    }

    // Postcode
    if (addr.postcode) {
      parts.push(addr.postcode);
    }

    return parts.length > 0 ? parts.join(', ') : item.display_name;
  };

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setShowSuggestions(true);
    setSelectedIndex(-1);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce the search (respect Nominatim 1 req/sec policy)
    debounceRef.current = setTimeout(() => {
      searchAddresses(newValue);
    }, 500);
  };

  // Handle suggestion selection
  const selectSuggestion = (suggestion) => {
    const selectedAddress = suggestion.short_address;
    setInputValue(selectedAddress);
    onChange(selectedAddress, suggestion);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          selectSuggestion(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  // Handle blur - allow manual entry
  const handleBlur = () => {
    // Small delay to allow click on suggestion
    setTimeout(() => {
      if (inputValue !== value) {
        onChange(inputValue, null);
      }
    }, 200);
  };

  // Clear input
  const handleClear = () => {
    setInputValue('');
    onChange('', null);
    setSuggestions([]);
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => inputValue.length >= 3 && setShowSuggestions(true)}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={cn('pl-10 pr-10', className)}
          autoComplete="off"
          {...props}
        />
        {loading && (
          <Loader2 className="absolute right-10 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-zinc-400" />
        )}
        {inputValue && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-zinc-100"
          >
            <X className="h-4 w-4 text-zinc-400" />
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-lg border border-zinc-200 shadow-lg max-h-60 overflow-auto">
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.id}
              className={cn(
                'p-3 cursor-pointer border-b border-zinc-100 last:border-0 transition-colors',
                index === selectedIndex
                  ? 'bg-zinc-100'
                  : 'hover:bg-zinc-50'
              )}
              onClick={() => selectSuggestion(suggestion)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-zinc-400 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-zinc-900 text-sm truncate">
                    {suggestion.short_address}
                  </p>
                  <p className="text-xs text-zinc-500 truncate">
                    {suggestion.display_name}
                  </p>
                </div>
              </div>
            </div>
          ))}
          <div className="p-2 text-center">
            <p className="text-xs text-zinc-400">
              Powered by OpenStreetMap
            </p>
          </div>
        </div>
      )}

      {/* No results message */}
      {showSuggestions && inputValue.length >= 3 && !loading && suggestions.length === 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-lg border border-zinc-200 shadow-lg p-4 text-center">
          <p className="text-sm text-zinc-500">No addresses found</p>
          <p className="text-xs text-zinc-400 mt-1">You can still type the address manually</p>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
